import { AfterViewInit, Component, EventEmitter, OnInit } from '@angular/core';
import { ECharts, EChartsOption } from 'echarts';
import { IDateRangePickerDashboardItem } from 'src/app/components/date-picker/date-picker.component';
import { IMultipleSelectDashboardItem } from 'src/app/components/multiple-select/multiple-select.component';
import { ChartColorService } from 'src/app/services/chart-color.service';
import { CommonLegendService } from 'src/app/services/common-legend.service';
import { IDashboardItemDataLoaderLink } from 'src/app/services/dashboard-item/IDashboardItemDataLoaderLink';
import { DashboardService } from 'src/app/services/dashboard/dashboard.service';
import { IDataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { DataLoaderFactories } from 'src/app/services/data-loader/data-loader.service';
import {
    IQueryContext,
    QueryContextDefaultSettings,
    QueryContextSettingID,
} from 'src/app/services/query-context/models/IQueryContext';
import { QueryContextService } from 'src/app/services/query-context/query-context.service';
import {
    DatasourceColumnId,
    datasourceColumnOrderByQuery,
} from 'src/app/utils/datasource-sql-map/datasource';
import { EChartsAxisHelper } from 'src/app/utils/echarts/echarts-axis-helper';
import { EChartsTooltipHelper } from 'src/app/utils/echarts/echarts-tooltip-helper';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { datePartDateTrunc } from 'src/app/utils/sql';
import { DatasourceColumnOldDataLoaderFactoryOptions } from '../common/data-loaders/datasource-column-old';
import { SegmentDatasourceColumnDataLoaderFactoryOptions } from '../common/data-loaders/segments';
import { DashboardItemFactories } from '../common/items/common-dashboard-items';
import { FilterCategoryDashboardItemFactoryOptions } from '../common/items/filter/category-filter-dashboard-item/filter-category';
import { createDefaultSegmentDatasourceColumn } from '../common/items/filter/segment-filter/segments';
import { ModifyTimeDashboardItemFactoryOptions } from '../common/items/modify-time';
import { IMultiSelectSettingDashboardItem } from '../common/items/setting/multi-select/multi-select.component';
import { ISingleSelectSettingDashboardItem } from '../common/items/setting/single-select/single-select.component';
import { StandardDashboardWorker } from '../common/standard-dashboard';
import { EngagementSessionsDistributionBySessionsDataLoaderFactory } from './data-loaders/engagement-sessions-distribution-by-sessions';
import { BiVizDashboardEngagement } from './sql-maps/bivz-engagement-sessions-with-dimensions-sql-map';
import { getGcpProjectIdForEnv } from 'src/app/services/data-environment/models/data-environment.model';

@Component({
    selector: 'app-lastarchon-engagement',
    templateUrl: './lastarchon-engagement.component.html',
    styleUrls: ['./lastarchon-engagement.component.scss'],
    providers: [DashboardService, QueryContextService, CommonLegendService],
})
export class LastArchonEngagementComponent
    extends StandardDashboardWorker
    implements OnInit, AfterViewInit
{
    override DASHBOARD_NAME = 'Engagement';
    override DASHBOARD_ID = 'lastarchon-engagement';

    values = Object.values;

    private gcpProjectId: GcpProjectId = 'am-lastarchon-dev';

    /////////////////////////////////////////////////////////////////
    ////
    ////    DATALOADERS
    ////    Some DataLoader can be used in multiple charts.
    ////    That's why there are created here and later referenced
    ////
    /////////////////////////////////////////////////////////////////

    override filterDataLoaders = {
        'lastarchon.engagement.filter.segment': DataLoaderFactories[
            'common.filter.segment'
        ]({
            createdInstanceId: 'lastarchon.engagement.filter_segments',
            gcpProjectId: () => {
                return getGcpProjectIdForEnv(this.dataEnv.current);
            },
            queryCacheService: this.queryCache,
            datasourceColumnId: 'SEGMENT',
        }).create(),
        'lastarchon.engagement.filter.activity_app_version':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId: 'lastarchon.engagement.filter.app_version',

                gcpProjectId: () => {
                    return getGcpProjectIdForEnv(this.dataEnv.current);
                },
                queryCacheService: this.queryCache,
                datasourceColumnId: 'ACTIVITY_APP_VERSION',
                datasourceSqlMap: BiVizDashboardEngagement.sqlMap,
                orderByQuery: datasourceColumnOrderByQuery(
                    'ACTIVITY_APP_VERSION',
                ),
            }).create(),
        'lastarchon.engagement.filter.activity_app_version_group_minor':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'lastarchon.engagement.filter.activity_app_version_group_minor',

                gcpProjectId: () => {
                    return getGcpProjectIdForEnv(this.dataEnv.current);
                },
                queryCacheService: this.queryCache,
                datasourceColumnId: 'ACTIVITY_APP_VERSION_GROUP_MINOR',
                datasourceSqlMap: BiVizDashboardEngagement.sqlMap,
                orderByQuery: datasourceColumnOrderByQuery(
                    'ACTIVITY_APP_VERSION_GROUP_MINOR',
                ),
            }).create(),
        'lastarchon.engagement.filter.first_geo_country_tier':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'lastarchon.engagement.filter.first_geo_country_tier',

                gcpProjectId: () => {
                    return getGcpProjectIdForEnv(this.dataEnv.current);
                },
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIRST_GEO_COUNTRY_TIER',
                datasourceSqlMap: BiVizDashboardEngagement.sqlMap,
                // filterContext: ['FIRST_GEO_COUNTRY'],
            }).create(),
        'lastarchon.engagement.filter.first_geo_country': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'lastarchon.engagement.filter.first_geo_country',

            gcpProjectId: () => {
                return getGcpProjectIdForEnv(this.dataEnv.current);
            },
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FIRST_GEO_COUNTRY',
            datasourceSqlMap: BiVizDashboardEngagement.sqlMap,
            filterContext: ['FIRST_GEO_COUNTRY_TIER'],
        }).create(),
        'lastarchon.engagement.filter.attribution_unified_traffic':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'lastarchon.engagement.filter.attribution_unified_traffic',

                gcpProjectId: () => {
                    return getGcpProjectIdForEnv(this.dataEnv.current);
                },
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIRST_ATTRIBUTION_UNIFIED_TRAFFIC',
                datasourceSqlMap: BiVizDashboardEngagement.sqlMap,
            }).create(),
        'lastarchon.engagement.filter.first_app_version': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'lastarchon.engagement.filter.first_app_version',

            gcpProjectId: () => {
                return getGcpProjectIdForEnv(this.dataEnv.current);
            },
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FIRST_APP_VERSION',
            datasourceSqlMap: BiVizDashboardEngagement.sqlMap,
            filterContext: true,
            orderByQuery: datasourceColumnOrderByQuery('FIRST_APP_VERSION'),
        }).create(),
        'lastarchon.engagement.filter.first_app_version_group_minor':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'lastarchon.engagement.filter.first_app_version_group_minor',

                gcpProjectId: () => {
                    return getGcpProjectIdForEnv(this.dataEnv.current);
                },
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIRST_APP_VERSION_GROUP_MINOR',
                datasourceSqlMap: BiVizDashboardEngagement.sqlMap,
                filterContext: true,
                orderByQuery: datasourceColumnOrderByQuery(
                    'FIRST_APP_VERSION_GROUP_MINOR',
                ),
            }).create(),
        'lastarchon.engagement.filter.etl_payer': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'lastarchon.engagement.filter.etl_payer',

            gcpProjectId: () => {
                return getGcpProjectIdForEnv(this.dataEnv.current);
            },
            queryCacheService: this.queryCache,
            datasourceColumnId: 'REVENUE_PAYER',
            datasourceSqlMap: BiVizDashboardEngagement.sqlMap,
        }).create(),
        'lastarchon.engagement.filter.etl_revenue_badge': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'lastarchon.engagement.filter.etl_revenue_badge',

            gcpProjectId: () => {
                return getGcpProjectIdForEnv(this.dataEnv.current);
            },
            queryCacheService: this.queryCache,
            datasourceColumnId: 'REVENUE_BADGE',
            datasourceSqlMap: BiVizDashboardEngagement.sqlMap,
            orderByQuery: datasourceColumnOrderByQuery('REVENUE_BADGE'),
        }).create(),
        'lastarchon.engagement.filter.attribution_network_name':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'lastarchon.engagement.filter.attribution_network_name',

                gcpProjectId: () => {
                    return getGcpProjectIdForEnv(this.dataEnv.current);
                },
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIRST_ATTRIBUTION_UNIFIED_NETWORK',
                datasourceSqlMap: BiVizDashboardEngagement.sqlMap,
            }).create(),
        'lastarchon.engagement.filter.attribution_campaign_name':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'lastarchon.engagement.filter.attribution_campaign_name',

                gcpProjectId: () => {
                    return getGcpProjectIdForEnv(this.dataEnv.current);
                },
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIRST_ATTRIBUTION_UNIFIED_CAMPAIGN',
                datasourceSqlMap: BiVizDashboardEngagement.sqlMap,
                filterContext: true,
            }).create(),
    };

    override chartDataLoaders = {
        'lastarchon.engagement.modify_time_engagement_datasource':
            DataLoaderFactories['common.modify_time']({
                createdInstanceId:
                    'lastarchon.engagement.modify_time_engagement_datasource',

                gcpProjectId: () => {
                    return getGcpProjectIdForEnv(this.dataEnv.current);
                },
                queryCacheService: this.queryCache,
                datasetId: 'bi_viz',
                tableId: 'dashboard_engagement',
            }).create(),

        'lastarchon.engagement.sessions_by_day_no': DataLoaderFactories[
            'common.engagement.sessions_by_day_no'
        ]({
            createdInstanceId: 'lastarchon.engagement.sessions_by_day_no',

            gcpProjectId: () => {
                return getGcpProjectIdForEnv(this.dataEnv.current);
            },
            queryCacheService: this.queryCache,
        }).create(),

        'lastarchon.engagement.sessions_by_date': DataLoaderFactories[
            'common.engagement.sessions_by_date'
        ]({
            createdInstanceId: 'lastarchon.engagement.sessions_by_date',

            gcpProjectId: () => {
                return getGcpProjectIdForEnv(this.dataEnv.current);
            },
            queryCacheService: this.queryCache,
        }).create(),

        'lastarchon.engagement.sessions_distribution_by_days_0.5min':
            DataLoaderFactories[
                'common.engagement.sessions_distribution_by_days'
            ]({
                createdInstanceId:
                    'lastarchon.engagement.sessions_distribution_by_days',

                gcpProjectId: () => {
                    return getGcpProjectIdForEnv(this.dataEnv.current);
                },
                queryCacheService: this.queryCache,
                granularity: '0.5min',
            }).create(),

        'lastarchon.engagement.sessions_distribution_by_days_5min':
            DataLoaderFactories[
                'common.engagement.sessions_distribution_by_days'
            ]({
                createdInstanceId:
                    'lastarchon.engagement.sessions_distribution_by_days',

                gcpProjectId: () => {
                    return getGcpProjectIdForEnv(this.dataEnv.current);
                },
                queryCacheService: this.queryCache,
                granularity: '5min',
            }).create(),

        'lastarchon.engagement.sessions_distribution_by_sessions_0.5min':
            DataLoaderFactories[
                'common.engagement.sessions_distribution_by_sessions'
            ]({
                createdInstanceId:
                    'lastarchon.engagement.sessions_distribution_by_sessions',

                gcpProjectId: () => {
                    return getGcpProjectIdForEnv(this.dataEnv.current);
                },
                queryCacheService: this.queryCache,
                granularity: '0.5min',
            }).create(),

        'lastarchon.engagement.sessions_distribution_by_sessions_5min':
            DataLoaderFactories[
                'common.engagement.sessions_distribution_by_sessions'
            ]({
                createdInstanceId:
                    'lastarchon.engagement.sessions_distribution_by_sessions',

                gcpProjectId: () => {
                    return getGcpProjectIdForEnv(this.dataEnv.current);
                },
                queryCacheService: this.queryCache,
                granularity: '5min',
            }).create(),
        // 'lastarchon.engagement.engagementdash_activity': DataLoaderFactories[
        //     'common.engagement.engagementdash_activity'
        // ]({
        //     createdInstanceId: 'lastarchon.engagement.engagementdash_activity',
        //
        // gcpProjectId: () => {
        //     return getGcpProjectIdForEnv(this.dataEnv.current);
        // },
        //     queryCacheService: this.queryCache,
        // }).create(),
        // 'lastarchon.engagement.engagementdash_30days_engagement': DataLoaderFactories[
        //     'common.engagement.engagementdash_30days_engagement'
        // ]({
        //     createdInstanceId: 'lastarchon.engagement.engagementdash_30days_engagement',
        //
        // gcpProjectId: () => {
        //     return getGcpProjectIdForEnv(this.dataEnv.current);
        // },
        //     queryCacheService: this.queryCache,
        // }).create(),
    };

    /////////////////////////////////////////////////////////////////
    ////
    ////    [DASHBOARD ITEM <-> DATALOADER] LINKS
    ////
    /////////////////////////////////////////////////////////////////
    settingSelectedDayAndSession: IMultiSelectSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.ENGAGEMENT_SELECTED_DAY_AND_SESSION,
            viewtype: 'multiSelect',
            displayName: 'Selected Day/Session',
            value:
                this.getInitialContext().settings[
                    QueryContextSettingID.ENGAGEMENT_SELECTED_DAY_AND_SESSION
                ] ?? [],
            selectOptions: [
                0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14,
            ].map((d) => ({
                value: d,
                label: `${d}`,
            })),
        },
    };

    settingItemDateAggregation: ISingleSelectSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.DATE_AGGREGATION,
            viewtype: 'singleSelect',
            displayName: 'Date aggregation',
            value: 'day' as datePartDateTrunc,
            selectOptions: ['day', 'week', 'month', 'quarter', 'year'] as
                | 'day'
                | datePartDateTrunc[],
        },
    };

    // CHARTS
    modify_time_engagement_datasource: IDashboardItemDataLoaderLink = {
        id: 'lnk.lastarchon.engagement.modify_time_engagement_datasource',
        dashboardItemFactory: DashboardItemFactories['common.modify_time'](
            new ModifyTimeDashboardItemFactoryOptions(
                'lastarchon.engagement.modify_time_engagement_datasource',
                'value',
            ),
        ),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'lastarchon.engagement.modify_time_engagement_datasource'
            ].emitter,
    };

    med_sess_length_by_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.lastarchon.engagement.chart.med_sess_length_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'Engagement (wip)',
            createdInstanceId:
                'lastarchon.engagement.chart.med_sess_length_by_day_no',
            dashboardItem: {
                title: 'Median session lenght per user by day number',
            },
            xAxisType: 'category',
            xAxisCategorySubtype: 'generic',
            xColumnLabel: 'day_no',
            yColumnLabel: 'median_sess_length_mins',
            metricName: 'Median session length',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            getEchartsOptionsOverride: (context: IQueryContext) => {
                return {
                    legend: { show: false },
                };
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts,
            ) => {
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        valueFormatter:
                            EChartsTooltipHelper.tooltipNumberValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['lastarchon.engagement.sessions_by_day_no']
                .emitter,
    };
    avg_no_sessions_by_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.lastarchon.engagement.chart.avg_no_sessions_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'engagement',
            createdInstanceId:
                'lastarchon.engagement.chart.avg_no_sessions_by_day_no',
            dashboardItem: {
                title: 'Number of sessions per user by day number',
            },
            xAxisType: 'category',
            xAxisCategorySubtype: 'generic',
            xColumnLabel: 'day_no',
            yColumnLabel: 'avg_sess_per_user',
            metricName: 'Avg sessions per user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            getEchartsOptionsOverride: (context: IQueryContext) => {
                return {
                    legend: { show: false },
                };
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts,
            ) => {
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        valueFormatter:
                            EChartsTooltipHelper.tooltipNumberValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['lastarchon.engagement.sessions_by_day_no']
                .emitter,
    };
    med_sess_length_by_date: IDashboardItemDataLoaderLink = {
        id: 'lnk.lastarchon.engagement.chart.med_sess_length_by_date',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'engagement',
            createdInstanceId:
                'lastarchon.engagement.chart.med_sess_length_by_date',
            dashboardItem: { title: 'Median session lenght per user by date' },
            xAxisType: 'time',
            xColumnLabel: 'date',
            yColumnLabel: 'median_sess_length_mins',
            metricName: 'Median session length',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            getEchartsOptionsOverride: (context: IQueryContext) => {
                return {
                    legend: { show: false },
                };
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts,
            ) => {
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter:
                            EChartsAxisHelper.createAxisLabelFormatter(
                                EChartsAxisHelper.axisLabelDateFormatter,
                                context,
                            ),
                        valueFormatter:
                            EChartsTooltipHelper.tooltipNumberValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['lastarchon.engagement.sessions_by_date']
                .emitter,
    };
    avg_no_sessions_by_date: IDashboardItemDataLoaderLink = {
        id: 'lnk.lastarchon.engagement.chart.avg_no_sessions_by_date',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'engagement',
            createdInstanceId:
                'lastarchon.engagement.chart.avg_no_sessions_by_date',
            dashboardItem: {
                title: 'Number of sessions per user by date',
                chartDocumentation: `
                <pre>COUNT(session_no) / COUNT(DISTINCT user_id)</pre>
                <p>
                Przy agregacji dat dzielimy przez liczbę distinct userów wybranej agregacji, czyli dla Date aggregation Month mamy  Liczbę sesji / MAU
                </p> `,
            },
            xAxisType: 'time',
            xColumnLabel: 'date',
            yColumnLabel: 'avg_sess_per_user',
            metricName: 'Avg sessions per user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            getEchartsOptionsOverride: (context: IQueryContext) => {
                return {
                    legend: { show: false },
                };
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts,
            ) => {
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter:
                            EChartsAxisHelper.createAxisLabelFormatter(
                                EChartsAxisHelper.axisLabelDateFormatter,
                                context,
                            ),
                        valueFormatter:
                            EChartsTooltipHelper.tooltipNumberValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['lastarchon.engagement.sessions_by_date']
                .emitter,
    };
    sessions_distribution_by_days_05min: IDashboardItemDataLoaderLink = {
        id: 'lnk.lastarchon.engagement.chart.sessions_distribution_by_days_05min',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'engagement',
            createdInstanceId:
                'lastarchon.engagement.chart.sessions_distribution_by_days_05min',
            dashboardItem: {
                title: (context) => {
                    const selectedDaysAndSessions =
                        context.settings['engagement.selectedDayAndSession'] ??
                        QueryContextDefaultSettings[
                            QueryContextSettingID
                                .ENGAGEMENT_SELECTED_DAY_AND_SESSION
                        ]!;
                    return `Session length distribution of selected days: ${selectedDaysAndSessions
                        .map((d) => `d${d}`)
                        .join(', ')}`;
                },
            },
            xColumnLabel: 'session_length_bucket',
            yColumnLabel: 'sample',
            metricName: 'Sessions',
            metricsGetter: (context: IQueryContext) => {
                const selectedDaysAndSessions = context.settings[
                    'engagement.selectedDayAndSession'
                ] ?? ['3'];
                const metrics: Record<
                    string,
                    { echartsSeriesOption: {}; name: string }
                > = {};
                selectedDaysAndSessions.forEach((d) => {
                    const metricName = `d${d}`;
                    metrics[`d${d}`] = {
                        echartsSeriesOption: {},
                        name: metricName,
                    };
                });
                return metrics;
            },
            echartsSeriesType: 'bar',
            xAxisType: 'category',
            xAxisCategorySubtype: 'generic',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            getEchartsOptionsOverride: (context: IQueryContext) => {
                return {
                    legend: { show: false },
                };
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts,
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) =>
                            // hardcoded histogram bin size 0.5min
                            `Duration: ${label} mins`,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipIntegerValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'lastarchon.engagement.sessions_distribution_by_days_0.5min'
            ].emitter,
    };
    sessions_distribution_by_sessions_05min: IDashboardItemDataLoaderLink = {
        id: 'lnk.lastarchon.engagement.chart.sessions_distribution_by_sessions_05min',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'engagement',
            createdInstanceId:
                'lastarchon.engagement.chart.sessions_distribution_by_sessions_05min',
            dashboardItem: {
                title: (context) => {
                    const selectedDaysAndSessions =
                        EngagementSessionsDistributionBySessionsDataLoaderFactory.getSelectedSessions(
                            context,
                        );
                    return `Session length distribution of selected sessions: ${selectedDaysAndSessions
                        .map((d) => `s${d}`)
                        .join(', ')}`;
                },
            },
            xColumnLabel: 'session_length_bucket',
            yColumnLabel: 'sample',
            metricName: 'Sessions',
            echartsSeriesType: 'bar',
            xAxisType: 'category',
            xAxisCategorySubtype: 'generic',
            metricsGetter: (context: IQueryContext) => {
                const selectedDaysAndSessions =
                    EngagementSessionsDistributionBySessionsDataLoaderFactory.getSelectedSessions(
                        context,
                    );
                const metrics: Record<
                    string,
                    { echartsSeriesOption: {}; name: string }
                > = {};
                selectedDaysAndSessions.forEach((d) => {
                    const metricName = `s${d}`;
                    metrics[`s${d}`] = {
                        echartsSeriesOption: {},
                        name: metricName,
                    };
                });
                return metrics;
            },
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            getEchartsOptionsOverride: (context: IQueryContext) => {
                return {
                    legend: { show: false },
                };
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts,
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) =>
                            // hardcoded histogram bin size 0.5min
                            `Duration: ${label} mins`,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipIntegerValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'lastarchon.engagement.sessions_distribution_by_sessions_0.5min'
            ].emitter,
    };
    sessions_distribution_by_days_5min: IDashboardItemDataLoaderLink = {
        id: 'lnk.lastarchon.engagement.chart.sessions_distribution_by_days_5min',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'engagement',
            createdInstanceId:
                'lastarchon.engagement.chart.sessions_distribution_by_days_5min',
            dashboardItem: {
                title: (context) => {
                    const selectedDaysAndSessions =
                        context.settings['engagement.selectedDayAndSession'] ??
                        QueryContextDefaultSettings[
                            QueryContextSettingID
                                .ENGAGEMENT_SELECTED_DAY_AND_SESSION
                        ]!;
                    return `Session length distribution of selected days: ${selectedDaysAndSessions
                        .map((d) => `d${d}`)
                        .join(', ')}`;
                },
            },
            xColumnLabel: 'session_length_bucket',
            yColumnLabel: 'sample',
            metricName: 'Sessions',
            metricsGetter: (context: IQueryContext) => {
                const selectedDaysAndSessions = context.settings[
                    'engagement.selectedDayAndSession'
                ] ?? ['3'];
                const metrics: Record<
                    string,
                    { echartsSeriesOption: {}; name: string }
                > = {};
                selectedDaysAndSessions.forEach((d) => {
                    const metricName = `d${d}`;
                    metrics[`d${d}`] = {
                        echartsSeriesOption: {},
                        name: metricName,
                    };
                });
                return metrics;
            },
            echartsSeriesType: 'bar',
            xAxisType: 'category',
            xAxisCategorySubtype: 'generic',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            getEchartsOptionsOverride: (context: IQueryContext) => {
                return {
                    legend: { show: false },
                };
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts,
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) =>
                            // hardcoded histogram bin size 0.5min
                            `Duration: ${label} mins`,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipIntegerValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'lastarchon.engagement.sessions_distribution_by_days_5min'
            ].emitter,
    };
    sessions_distribution_by_sessions_5min: IDashboardItemDataLoaderLink = {
        id: 'lnk.lastarchon.engagement.chart.sessions_distribution_by_sessions_5min',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'engagement',
            createdInstanceId:
                'lastarchon.engagement.chart.sessions_distribution_by_sessions_5min',
            dashboardItem: {
                title: (context) => {
                    const selectedDaysAndSessions =
                        EngagementSessionsDistributionBySessionsDataLoaderFactory.getSelectedSessions(
                            context,
                        );
                    return `Session length distribution of selected sessions: ${selectedDaysAndSessions
                        .map((d) => `s${d}`)
                        .join(', ')}`;
                },
            },
            xColumnLabel: 'session_length_bucket',
            yColumnLabel: 'sample',
            metricName: 'Sessions',
            echartsSeriesType: 'bar',
            xAxisType: 'category',
            xAxisCategorySubtype: 'generic',
            metricsGetter: (context: IQueryContext) => {
                const selectedDaysAndSessions =
                    EngagementSessionsDistributionBySessionsDataLoaderFactory.getSelectedSessions(
                        context,
                    );
                const metrics: Record<
                    string,
                    { echartsSeriesOption: {}; name: string }
                > = {};
                selectedDaysAndSessions.forEach((d) => {
                    const metricName = `s${d}`;
                    metrics[`s${d}`] = {
                        echartsSeriesOption: {},
                        name: metricName,
                    };
                });
                return metrics;
            },
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            getEchartsOptionsOverride: (context: IQueryContext) => {
                return {
                    legend: { show: false },
                };
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts,
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) =>
                            // hardcoded histogram bin size 0.5min
                            `Duration: ${label} mins`,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipIntegerValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'lastarchon.engagement.sessions_distribution_by_sessions_5min'
            ].emitter,
    };

    // FILTERS
    filterDashboardItemLinks: Partial<
        Record<DatasourceColumnId, IDashboardItemDataLoaderLink>
    > = {
        ACTIVITY_DATE: {
            id: 'lastarchon.engagement.filter.activity_date',
            dashboardItemFactory: DashboardItemFactories['filter.daterange']({
                createdInstanceId: 'lastarchon.engagement.filter.activity_date',
                datasourceColumnId: 'ACTIVITY_DATE',
                splitable: false,
            }),
            // empty emitter DataRangePicker doesn't need data
            dataLoaderEmitter: new EventEmitter<IDataLoaderResult>(),
        },
        ACTIVITY_APP_VERSION_GROUP_MINOR: {
            id: 'lastarchon.engagement.filter.app_version_group_minor',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'lastarchon.engagement.filter.app_version_group_minor',
                    'ACTIVITY_APP_VERSION_GROUP_MINOR',
                ),
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'lastarchon.engagement.filter.activity_app_version_group_minor'
                ].emitter,
        },
        ACTIVITY_APP_VERSION: {
            id: 'lastarchon.engagement.filter.app_version',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'lastarchon.engagement.filter.app_version',
                    'ACTIVITY_APP_VERSION',
                ),
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'lastarchon.engagement.filter.activity_app_version'
                ].emitter,
        },
        SEGMENT: {
            id: 'lastarchon.engagement.filter.segments',
            dashboardItemFactory: DashboardItemFactories['filter.segments']({
                createdInstanceId: 'lastarchon.engagement.filter.segments',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders['lastarchon.engagement.filter.segment']
                    .emitter,
        },
        FIRST_SESSION_DATE: {
            id: 'lastarchon.engagement.filter.FIRST_SESSION_DATE',
            dashboardItemFactory: DashboardItemFactories['filter.daterange']({
                createdInstanceId:
                    'lastarchon.engagement.filter.FIRST_SESSION_DATE',
                datasourceColumnId: 'FIRST_SESSION_DATE',
                splitable: true,
            }),
            // empty emitter DataRangePicker doesn't need data
            dataLoaderEmitter: new EventEmitter<IDataLoaderResult>(),
        },
        FIRST_GEO_COUNTRY_TIER: {
            id: 'lastarchon.engagement.filter.first_geo_country_tier',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'lastarchon.engagement.filter.first_geo_country_tier',
                    'FIRST_GEO_COUNTRY_TIER',
                ),
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'lastarchon.engagement.filter.first_geo_country_tier'
                ].emitter,
        },
        FIRST_GEO_COUNTRY: {
            id: 'lastarchon.engagement.filter.first_geo_country',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'lastarchon.engagement.filter.first_geo_country',
                    'FIRST_GEO_COUNTRY',
                ),
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'lastarchon.engagement.filter.first_geo_country'
                ].emitter,
        },
        FIRST_ATTRIBUTION_UNIFIED_TRAFFIC: {
            id: 'lastarchon.engagement.filter.attribution_unified_traffic',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'lastarchon.engagement.filter.attribution_unified_traffic',
                    'FIRST_ATTRIBUTION_UNIFIED_TRAFFIC',
                ),
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'lastarchon.engagement.filter.attribution_unified_traffic'
                ].emitter,
        },
        FIRST_APP_VERSION_GROUP_MINOR: {
            id: 'lastarchon.engagement.filter.first_app_version_group_minor',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'lastarchon.engagement.filter.first_app_version_group_minor',
                    'FIRST_APP_VERSION_GROUP_MINOR',
                ),
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'lastarchon.engagement.filter.first_app_version_group_minor'
                ].emitter,
        },
        FIRST_APP_VERSION: {
            id: 'lastarchon.engagement.filter.first_app_version',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'lastarchon.engagement.filter.first_app_version',
                    'FIRST_APP_VERSION',
                ),
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'lastarchon.engagement.filter.first_app_version'
                ].emitter,
        },
        REVENUE_PAYER: {
            id: 'lastarchon.engagement.filter.etl_payer',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'lastarchon.engagement.filter.etl_payer',
                    'REVENUE_PAYER',
                ),
            ),
            dataLoaderEmitter:
                this.filterDataLoaders['lastarchon.engagement.filter.etl_payer']
                    .emitter,
        },
        REVENUE_BADGE: {
            id: 'lastarchon.engagement.filter.etl_revenue_badge',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'lastarchon.engagement.filter.etl_revenue_badge',
                    'REVENUE_BADGE',
                ),
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'lastarchon.engagement.filter.etl_revenue_badge'
                ].emitter,
        },
        FIRST_ATTRIBUTION_UNIFIED_NETWORK: {
            id: 'lastarchon.engagement.filter.attribution_network_name',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'lastarchon.engagement.filter.attribution_network_name',
                    'FIRST_ATTRIBUTION_UNIFIED_NETWORK',
                ),
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'lastarchon.engagement.filter.attribution_network_name'
                ].emitter,
        },
        FIRST_ATTRIBUTION_UNIFIED_CAMPAIGN: {
            id: 'lastarchon.engagement.filter.attribution_campaign_name',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'lastarchon.engagement.filter.attribution_campaign_name',
                    'FIRST_ATTRIBUTION_UNIFIED_CAMPAIGN',
                ),
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'lastarchon.engagement.filter.attribution_campaign_name'
                ].emitter,
        },
    };

    overridengOnInit() {
        super.ngOnInit();
        console.log({
            msg: 'LastArchonEngagementComponent.ngOnInit',
            cancellationToken: this.cancellationToken,
        });
    }
    ngAfterViewInit() {
        console.log({
            msg: 'LastArchonEngagementComponent.ngAfterViewInit',
            cancellationToken: this.cancellationToken,
        });

        this.standardDashboardInitialize();
    }

    override getInitialContext(): IQueryContext {
        const initialContext = QueryContextService.INITIAL_CONTEXT;

        const firebaseCountryTierIC: IMultipleSelectDashboardItem['datasourceColumn'] =
            {
                id: 'FIRST_GEO_COUNTRY_TIER',
                state: {
                    value: {
                        options: ['Tier 1', 'Tier 2', 'Tier 3'],
                        filterType: 'include',
                    },
                    // filterType: 'include',
                    isFilterApplied: true,
                    isSplitApplied: false,
                },
            };
        const activityDateIC: IDateRangePickerDashboardItem['datasourceColumn'] =
            {
                id: 'ACTIVITY_DATE',
                state: {
                    value: {
                        preset: 'last 365 days',
                        splitAggregate: 'day',
                    },
                    isFilterApplied: true,
                    isSplitApplied: false,
                },
            };
        const segmentIC = createDefaultSegmentDatasourceColumn();

        // set initialContext for AppStore to Google Play
        initialContext.datasourceColumns = [
            // firebaseAppStoreIC,
            // firebaseCountryTierIC,
            activityDateIC,
            segmentIC,
        ];

        initialContext.settings = {
            [QueryContextSettingID.ENGAGEMENT_SELECTED_DAY_AND_SESSION]:
                QueryContextDefaultSettings[
                    QueryContextSettingID.ENGAGEMENT_SELECTED_DAY_AND_SESSION
                ]!,
        };
        return initialContext;
    }
}
